import { useQuery } from '@tanstack/react-query';
import { usePatientDetailContext, useDrawerContext } from 'context/hooks';
import { MEASUREMENT_REFETCH_INTERVAL, queryKeys, queryParams } from 'utils/query';
import {
  reviewHeartRhythmMeasurement,
  getHeartRhythmMeasurementDetails,
  GetHeartRhythmMeasurementDetailsParams,
  getHeartRhythmMeasurementIdByTimestamp,
  getHeartRhythmMeasurements,
} from 'services/measurements/heartRhythm';
import { CallBackProps } from 'models/general';
import { HeartRhythmMeasurement, HeartRhythmMeasurementIndicatorMap } from 'models/heartRhythm/measurement';
import { useParams } from 'react-router-dom';
import { DistributionValues } from 'models/charts';
import { Indicator } from 'utils/models';
import { useCallback, useEffect, useRef } from 'react';
import { useParameters } from 'hooks/useParameters';
import { t } from 'i18next';
import { renderMeasurementDetails } from 'pages/Main/Patients/Details/Overview/HeartRateOverview/MeasurementDetails/MeasurementDetails.helpers';
import { distributionMeasurementIndicators } from 'utils/measurement';
import { usePatient } from './useUser';
import { useCustomMutation } from './useCustomMutation';

export const useHeartRhythmMeasurementDetails = (variables: GetHeartRhythmMeasurementDetailsParams) => {
  return useQuery({
    queryKey: [queryKeys.heartRhythmMeasurement, variables.measurementId],
    queryFn: () => getHeartRhythmMeasurementDetails(variables),
    enabled: !!variables.measurementId,
  });
};

export const useOpenHeartRhythmMeasurementDetail = () => {
  const { openDrawer } = useDrawerContext();
  return useCustomMutation({
    fn: getHeartRhythmMeasurementIdByTimestamp,
    onSuccess: {
      callback: (fetchedMeasurementId) => {
        openDrawer({
          content: renderMeasurementDetails(),
          detailId: fetchedMeasurementId,
        });
      },
    },
  });
};

export const useHeartRhythmOverview = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { startDate, endDate } = usePatientDetailContext();
  const { data: patientData } = usePatient({ patientId });
  const { openDrawer } = useDrawerContext();
  const { parameters } = useParameters();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const detailId = parameters[queryParams.detailId];
    if (detailId) {
      openDrawer({ content: renderMeasurementDetails(), detailId });
    }
  }, [parameters, openDrawer]);

  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.heartRhythmData, patientId, startDate, endDate],
    queryFn: () =>
      getHeartRhythmMeasurements({ patientId, startDate, endDate, groupIds: patientData?.visibleEnlistedGroupsIds }),
    refetchInterval: MEASUREMENT_REFETCH_INTERVAL,
    enabled: !!patientId,
  });

  const distributionValues: DistributionValues<Indicator> = {
    total: data?.length || 0,
    values: distributionMeasurementIndicators().map((indicator) => {
      return {
        label: indicator,
        total:
          data?.filter((measurement) => measurement.data.indicator && indicator.includes(measurement.data.indicator))
            .length || 0,
        color: HeartRhythmMeasurementIndicatorMap[indicator].color,
        name: t(`indicator_label_${indicator}`),
        Icon: HeartRhythmMeasurementIndicatorMap[indicator].Icon,
      };
    }),
  };

  const getItemById = useCallback(
    (id: string) => {
      return data?.find((item) => item.id === id);
    },
    [data],
  );

  const handleSelected = (item: HeartRhythmMeasurement) => {
    if (item.id) {
      openDrawer({
        content: renderMeasurementDetails(data?.map((value) => value.id)),
        detailId: item.id,
      });
    }
  };

  return {
    isLoading,
    data,
    distributionValues,
    getItemById,
    handleSelected,
  };
};

export const useReviewHeartRhythmMeasurement = ({ callback }: CallBackProps) => {
  return useCustomMutation({
    fn: reviewHeartRhythmMeasurement,
    onSuccess: {
      callback,
      invalidateQueryKeys: [[queryKeys.heartRhythmData]],
    },
  });
};
